import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import { useGetBranchListApi } from 'apis/restaurants/use-get-branch-list-api';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import type { UseMutationOptions, UseMutationResult } from 'react-query';

const endpoint = 'api/v1/restaurants/branches';
export const createBranchEndpointIdentifier = endpoint;

interface BodyType {
  name: {
    en: string;
    ar: string;
  };
  address: string | null;
  email: string | null;
  landlineNumber: string | null;
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  body: BodyType;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useCreateBranchApi(restaurantId: string, options?: Options): ReturnType {
  const { refetch: refetchBranches } = useGetBranchListApi(restaurantId, { enabled: false });
  const toast = useCustomToast();
  let fullEndpoint;

  const mutationFn = async ({ body }: TVariables) => {
    try {
      fullEndpoint = `${endpoint}/${restaurantId}`;

      const { data } = await http.post(fullEndpoint, body);

      // Refetch branches list
      refetchBranches();

      toast({
        description: 'New branch have been created successfully',
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { mutationKey: fullEndpoint, ...options });
}
